import { DateValue } from '@internationalized/date';
import { RangeValue } from '@react-types/shared';
import { useCallback } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import { UserSessionStatus } from 'dashboard/models/user-session';
import { showMessage } from 'dashboard/redux/actions/messages';
import { setCurrentPage, setFilters, setPageSize } from 'dashboard/redux/actions/user-sessions';
import { RootState } from 'dashboard/redux/store';
import { loadUserList } from 'dashboard/redux/thunk-actions/user';
import {
  expireSession,
  expireSessionsForUsers,
  loadUserSessionList,
} from 'dashboard/redux/thunk-actions/user-sessions';
import DashboardUserSessionsView from 'dashboard/views/DashboardUserSessionsView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useTable from 'lib/hooks/useTable';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';
import { translateDateValuetoLuxon } from 'lib/utils/filters';

const DashboardUserSessionsRoute = (props: PropsFromRedux) => {
  const { currentPage, filters, pageSize } = props;

  const { handlePageLinkClick, handlePageSizeClick, handleDateUpdate } = useTable({
    filters: filters,
    pageSize: pageSize,
    currentPage: currentPage,
    setFilters: props.setFilters,
    setCurrentPage: props.setCurrentPage,
    setPageSize: props.setPageSize,
  });

  const handleDateUpdateV2 = useCallback(
    (newDateRange: RangeValue<DateValue> | null) => {
      if (newDateRange) {
        const { startDate, endDate } = translateDateValuetoLuxon(newDateRange);
        if (startDate || endDate) handleDateUpdate({ startDate, endDate });
      }
    },
    [handleDateUpdate],
  );

  const handleUserFilterUpdate = useCallback((userIds: string[]) => {
    props.setFilters({
      userIds,
    });
    props.setCurrentPage(1);
  }, []);

  const handleStatusFilterUpdate = useCallback((status: UserSessionStatus) => {
    props.setFilters({
      status,
    });
    props.setCurrentPage(1);
  }, []);

  const { loading: userSessionsLoading } = useApiLoader({
    loader: () =>
      props.loadUserSessionList({
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        currentPage,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        pageSize,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        filters: filters,
      }),
    deps: [currentPage, pageSize, filters],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load user sessions.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: usersLoading } = useApiLoader({
    loader: () => props.loadUserList(),
    deps: [],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load users.');
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  const {
    loading: expireSessionLoading,
    error: expireSessionError,
    callback: expireSession,
  } = useApiCallback(
    async (userSessionId: string) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      await props.expireSession(userSessionId, { currentPage, pageSize, filters: filters });
      props.showMessage('Successfully expired session.', MessageType.Success);
    },
    [currentPage, pageSize, filters],
  );

  useApiErrorHandler({
    error: expireSessionError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to expire user session.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  const {
    loading: expireSessionsForUsersLoading,
    error: expireSessionsForUsersError,
    callback: expireSessionsForUsers,
  } = useApiCallback(
    async (userIds: string[]) => {
      await props.expireSessionsForUsers(userIds, {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        currentPage,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        pageSize,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        filters: filters,
      });
      props.showMessage('Successfully expired sessions.', MessageType.Success);
    },
    [currentPage, pageSize, filters],
  );

  useApiErrorHandler({
    error: expireSessionsForUsersError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to expire sessions for user.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  return (
    <DashboardUserSessionsView
      currentUser={props.currentUser}
      currentUserSession={props.currentUserSession}
      userSessions={props.userSessions}
      users={props.users}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      filters={filters}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      paginationParams={props.paginationParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pageSize={pageSize}
      isLoading={
        userSessionsLoading || usersLoading || expireSessionLoading || expireSessionsForUsersLoading
      }
      onPageLinkClick={handlePageLinkClick}
      onPageSizeClick={handlePageSizeClick}
      onDateUpdate={handleDateUpdateV2}
      onUserFilterUpdate={handleUserFilterUpdate}
      onStatusFilterUpdate={handleStatusFilterUpdate}
      onExpire={expireSession}
      onExpireAll={expireSessionsForUsers}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    userSessions: state.userSessions.userSessions,
    users: state.user.users,
    currentUser: state.currentUser.currentUser,
    currentUserSession: state.currentUserSession.userSession,
    pageSize: state.userSessions.pageSize,
    currentPage: state.userSessions.currentPage,
    paginationParams: state.userSessions.paginationParams,
    filters: state.userSessions.filters,
  };
};

const mapDispatchToProps = {
  loadUserSessionList,
  expireSession,
  expireSessionsForUsers,
  loadUserList,
  setFilters,
  setPageSize,
  setCurrentPage,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardUserSessionsRoute);
